import { AnonymousUser } from '../types/database';

const ANIMALS = [
  'Bear', 'Wolf', 'Fox', 'Rabbit', 'Deer', 'Owl', 'Eagle', 'Hawk', 'Swan', 'Duck',
  'Cat', 'Dog', 'Horse', 'Lion', 'Tiger', 'Elephant', 'Giraffe', 'Zebra', 'Panda', 'Koala',
  'Penguin', 'Dolphin', 'Whale', 'Shark', 'Turtle', 'Frog', 'Butterfly', 'Bee', 'Ant', 'Spider',
  'Dragon', 'Phoenix', 'Unicorn', 'Griffin', 'Pegasus', 'Lynx', 'Jaguar', 'Leopard', 'Cheetah', 'Panther',
  'Raven', 'Crow', 'Sparrow', 'Robin', 'Cardinal', 'Falcon', 'Kestrel', 'Heron', 'Crane', 'Peacock'
];

const COLORS = [
  'Red', 'Blue', 'Green', 'Yellow', 'Purple', 'Orange', 'Pink', 'Cyan', 'Magenta', 'Teal',
  'Silver', 'Gold', 'Bronze', 'Coral', 'Indigo', 'Violet', 'Crimson', 'Emerald', 'Sapphire', 'Ruby',
  'Amber', 'Jade', 'Ivory', 'Onyx', 'Pearl', 'Copper', 'Steel', 'Frost', 'Storm', 'Dawn'
];

export class AnonymousUserService {
  private static anonymousUsers = new Map<string, AnonymousUser>();

  /**
   * Generate a random anonymous user name like "Anonymous Red Fox"
   */
  static generateAnonymousName(): string {
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    const animal = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
    return `Anonymous ${color} ${animal}`;
  }

  /**
   * Create a new anonymous user session
   */
  static createAnonymousUser(): AnonymousUser {
    const id = this.generateSessionId();
    const animalName = this.generateAnonymousName();
    const now = new Date().toISOString();

    const anonymousUser: AnonymousUser = {
      id,
      animal_name: animalName,
      session_start: now,
      last_activity: now,
    };

    this.anonymousUsers.set(id, anonymousUser);
    return anonymousUser;
  }

  /**
   * Update the last activity time for an anonymous user
   */
  static updateActivity(sessionId: string): void {
    const user = this.anonymousUsers.get(sessionId);
    if (user) {
      user.last_activity = new Date().toISOString();
    }
  }

  /**
   * Get an anonymous user by session ID
   */
  static getAnonymousUser(sessionId: string): AnonymousUser | undefined {
    return this.anonymousUsers.get(sessionId);
  }

  /**
   * Get all active anonymous users (active within last 5 minutes)
   */
  static getActiveAnonymousUsers(): AnonymousUser[] {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    return Array.from(this.anonymousUsers.values()).filter(
      user => user.last_activity > fiveMinutesAgo
    );
  }

  /**
   * Clean up inactive anonymous users (inactive for more than 30 minutes)
   */
  static cleanupInactiveUsers(): void {
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();

    // Convert entries to array to avoid iterator issues
    const entries = Array.from(this.anonymousUsers.entries());
    for (const [sessionId, user] of entries) {
      if (user.last_activity < thirtyMinutesAgo) {
        this.anonymousUsers.delete(sessionId);
      }
    }
  }

  /**
   * Generate a unique session ID
   */
  private static generateSessionId(): string {
    return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize cleanup interval (call once in app startup)
   */
  static initializeCleanup(): void {
    // Clean up inactive users every 10 minutes
    setInterval(() => {
      this.cleanupInactiveUsers();
    }, 10 * 60 * 1000);
  }
}